import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/features/auth/auth-context'
import { fetchMyTasks, NO_TASKS, type MyTasks, type TaskLock } from './tasks-api'
import { tasksEnabled } from './tasks-flag'

/** The student's tasks: one cache entry shared by the Home card, the library banner and the test gate. Always asks the server. */
export const myTasksQuery = {
  queryKey: ['student', 'my-tasks'] as const,
  queryFn: fetchMyTasks,
  staleTime: 0,
  retry: false,
}

/** Only a signed-in student asks, never the sample session, and never in a build without the flag. */
export function useTasksAudience(): boolean {
  const { profile, demo } = useAuth()
  return tasksEnabled() && !demo && profile?.user?.role === 'student'
}

export function useMyTasks(options: { refetchOnWindowFocus?: boolean } = {}) {
  const audience = useTasksAudience()
  return useQuery({ ...myTasksQuery, enabled: audience, ...options })
}

/** The lock on other practice, or null: no lock, a failed lookup, a student who is not asked, or a flag that is off. */
export function useTaskLock(): { lock: TaskLock | null; data: MyTasks; ready: boolean } {
  const query = useMyTasks()
  const data = query.isError ? NO_TASKS : (query.data ?? NO_TASKS)
  return { lock: data.lock, data, ready: query.data !== undefined }
}
